import React, { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useContent } from "../../hooks/useContent";
import { API_BASE } from "../../lib/api";

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 400]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  const { content, loading } = useContent("hero");

  // Fallback defaults
  const title1 = content?.title_line_1 || "DIGITAL";
  const title2 = content?.title_line_2 || "ALCHEMY";
  const subtext = content?.subtext || "We craft immersive digital experiences for the brands of tomorrow.";
  const title1Enabled = content?.title_line_1_enabled !== false;
  const title2Enabled = content?.title_line_2_enabled !== false;
  const subtextEnabled = content?.subtext_enabled !== false;
  const videoEnabled = content?.video_enabled ?? true;
  const soundEnabled = content?.sound_enabled ?? false;

  // Convert Google Drive share links to use backend proxy + add cache busting
  const getPlayableVideoUrl = (url) => {
    // No fallback - if no URL is set, video won't show
    if (!url) return null;

    const cacheBuster = btoa(url).slice(0, 10);

    const isDriveLink = url.includes("drive.google.com");

    if (isDriveLink) {
      return `${API_BASE}/api/proxy-video?url=${encodeURIComponent(url)}&v=${cacheBuster}`;
    }

    // Add cache-busting for all video URLs
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${cacheBuster}`;
  };

  const videoUrl = getPlayableVideoUrl(content?.video_url);

  const toggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <section className="relative h-[100dvh] w-full flex flex-col justify-center items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {/* Video Background (conditional) */}
        {videoEnabled && videoUrl && (
          <video
            key={videoUrl}
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}

        {/* Dark Overlay for text visibility */}
        <div className={`absolute inset-0 ${videoEnabled ? 'bg-black/60' : 'bg-gradient-to-b from-black via-gray-900 to-black opacity-90'}`} />

        {/* Gradient fade on edges */}
        {videoEnabled && <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80" />}

        {/* Animated accent glow */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-accent/20 rounded-full blur-[100px] md:blur-[150px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 5, repeat: Infinity, repeatType: "reverse" }}
        />
      </div>

      {/* Sound Toggle Button - minimal design matching website aesthetic */}
      {videoEnabled && soundEnabled && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 0.5 }}
          onClick={toggleSound}
          className="absolute bottom-10 left-4 md:left-10 z-30 flex items-center gap-3 group cursor-pointer mix-blend-difference"
        >
          {/* Animated sound indicator */}
          <div className="flex items-center gap-1">
            {isMuted ? (
              // Muted state - static bars
              <>
                <div className="w-0.5 h-2 bg-white/50 rounded-full" />
                <div className="w-0.5 h-3 bg-white/50 rounded-full" />
                <div className="w-0.5 h-2 bg-white/50 rounded-full" />
              </>
            ) : (
              // Unmuted state - animated bars
              [1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="w-0.5 bg-accent rounded-full"
                  animate={{ height: [8, 16, 8] }}
                  transition={{
                    duration: 0.4,
                    repeat: Infinity,
                    delay: i * 0.1,
                    ease: "easeInOut"
                  }}
                />
              ))
            )}
          </div>
          <span className="text-xs uppercase tracking-widest text-white group-hover:text-accent transition-colors">
            {isMuted ? "Sound" : "Mute"}
          </span>
        </motion.button>
      )}

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 text-center flex flex-col items-center px-4 w-full"
      >
        {title1Enabled && (
          <motion.h1
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="text-[13vw] sm:text-[12vw] lg:text-[11vw] leading-[0.85] font-display font-bold text-white tracking-tighter mix-blend-overlay w-full"
          >
            {title1}
          </motion.h1>
        )}
        {title2Enabled && (
          <motion.h1
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.65, ease: [0.76, 0, 0.24, 1] }}
            className="text-[13vw] sm:text-[12vw] lg:text-[11vw] leading-[0.85] font-display font-bold text-transparent text-stroke hover:text-accent transition-colors duration-500 cursor-default w-full"
          >
            {title2}
          </motion.h1>
        )}

        {subtextEnabled && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-8 md:mt-12 flex items-center justify-center md:justify-start gap-8 px-4"
          >
            <p className="text-gray-400 max-w-[280px] md:max-w-sm text-center md:text-left text-xs md:text-sm uppercase tracking-widest font-medium md:border-l border-accent md:pl-4">
              {subtext}
            </p>
          </motion.div>
        )}
      </motion.div>

      <motion.div
        className="absolute bottom-10 right-4 md:right-10 flex flex-col items-end gap-1 z-20 mix-blend-difference hidden md:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <span className="text-xs uppercase tracking-widest text-white">Scroll to explore</span>
        <div className="w-px h-16 bg-white/50 overflow-hidden">
          <motion.div
            className="w-full h-full bg-accent"
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;

